import cx from 'classnames'
import {
  type CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as THREE from 'three'
import { debounce } from 'throttle-debounce'
import { useIsomorphicLayoutEffect } from 'usehooks-ts'

import {
  type FrameViewerCamera,
  createCamera,
  fitCameraToObject,
} from '@frame-builder/lib/frame-viewer/camera'
import {
  generateGeometry,
  updateGeometry,
} from '@frame-builder/lib/frame-viewer/geometry'
import { getObjectSizeVector } from '@frame-builder/lib/frame-viewer/helpers'
import { generateTextureUrlFromImage } from '@frame-builder/lib/frame-viewer/image'
import {
  createAmbientLight,
  createDirectionalLight,
} from '@frame-builder/lib/frame-viewer/light'
import { createRenderer } from '@frame-builder/lib/frame-viewer/renderer'
import { createScene } from '@frame-builder/lib/frame-viewer/scene'
import { type FrameSettings } from '@frame-builder/lib/frame-viewer/settings'
import { UVMapViewer } from '@frame-builder/lib/frame-viewer/uv-map-viewer'
import {
  getWallTexture,
  wallHeight,
  wallWidth,
} from '@frame-builder/lib/frame-viewer/wall'
import { shareRef } from '@frame-builder/lib/helpers'
import { useSpring } from '@frame-builder/lib/spring'

interface FrameViewerProps {
  frameSettings: FrameSettings
  camera: FrameViewerCamera
  width?: number
  height?: number
  backgroundColor?: string
  // Set this to true, to enable UV debugger for frame
  debugFrameUv?: boolean
  // Set this to true, to enable UV debugger for artwork
  debugArtworkUv?: boolean
  className?: string
  style?: CSSProperties
}

const FrameViewer = forwardRef<HTMLCanvasElement, FrameViewerProps>(
  (
    {
      frameSettings,
      camera,
      width,
      height,
      backgroundColor,
      debugFrameUv = false,
      debugArtworkUv = false,
      className,
      style,
    },
    forwardedRef,
  ) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)

    const textureLoaderRef = useRef(new THREE.TextureLoader())
    const sceneRef = useRef<THREE.Scene>()
    const cameraRef = useRef<THREE.PerspectiveCamera>()
    const rendererRef = useRef<THREE.WebGLRenderer>()
    const directionalLightRef = useRef<THREE.DirectionalLight>()

    const frameGeometryRef = useRef<THREE.Geometry>()
    const frameMaterialRef = useRef<THREE.MeshStandardMaterial>()
    const frameMeshRef = useRef<THREE.Mesh>()

    const passepartoutGeometryRef = useRef<THREE.Geometry>()
    const passepartoutCoreGeometryRef = useRef<THREE.Geometry>()
    const passepartoutMaterialRef = useRef<THREE.MeshBasicMaterial>()
    const passepartoutCoreMaterialRef = useRef<THREE.MeshBasicMaterial>()
    const passepartoutMeshRef = useRef<THREE.Mesh>()
    const passepartoutCoreMeshRef = useRef<THREE.Mesh>()

    const artworkGeometryRef = useRef<THREE.Geometry>()
    const artworkMaterialRef = useRef<THREE.MeshStandardMaterial>()
    const artworkMeshRef = useRef<THREE.Mesh>()

    const wallMeshRef = useRef<THREE.Mesh>()
    const wallMaterialRef = useRef<THREE.MeshBasicMaterial>()
    const wallGeometryRef = useRef<THREE.PlaneGeometry>()

    const frameUvViewerRef = useRef<UVMapViewer>()
    const artworkUvViewerRef = useRef<UVMapViewer>()

    const lastWidthRef = useRef<number | undefined>()
    const lastHeightRef = useRef<number | undefined>()

    const [artworkUrl, setArtworkUrl] = useState<string | null>(
      frameSettings.artworkUrl ?? null,
    )
    const [artworkTexture, setArtworkTexture] = useState<THREE.Texture | null>(
      null,
    )
    const [frameDiffuseUrl, setFrameDiffuseUrl] = useState<string | undefined>(
      frameSettings.frameDiffuseUrl,
    )
    const [frameDiffuseTexture, setFrameDiffuseTexture] =
      useState<THREE.Texture | null>(null)

    // Camera zoom and rotation
    const defaultMaxDimensionTarget = Math.max(
      frameSettings.artworkWidth +
        frameSettings.passepartoutLeftSize +
        frameSettings.passepartoutRightSize +
        frameSettings.frameLeftSize +
        frameSettings.frameRightSize,
      frameSettings.artworkHeight +
        frameSettings.passepartoutTopSize +
        frameSettings.passepartoutBottomSize +
        frameSettings.frameTopSize +
        frameSettings.frameBottomSize,
    )
    const [maxDimensionTarget, setMaxDimensionTarget] = useState(
      defaultMaxDimensionTarget,
    )
    const [rotationTarget, setRotationTarget] = useState(0)
    const maxDimension = useSpring(maxDimensionTarget, 8, 10)
    const rotation = useSpring(rotationTarget, 1, 10)

    const [isCanvasRefReady, setIsCanvasRefReady] = useState(false)

    const [activeWidth, setActiveWidth] = useState(width)
    const [activeHeight, setActiveHeight] = useState(height)

    // Ready state hook
    useIsomorphicLayoutEffect(() => {
      setIsCanvasRefReady(true)
    }, [])

    // Active width hook
    useEffect(() => {
      if (activeWidth === width) {
        return
      }

      // Stop if delta is smaller than 10 pixels
      if (activeWidth && width && Math.abs(activeWidth - width) < 10) {
        return
      }

      setActiveWidth(width)
    }, [activeWidth, width])

    // Active height hook
    useEffect(() => {
      if (activeHeight === height) {
        return
      }

      // Stop if delta is smaller than 10 pixels
      if (activeHeight && height && Math.abs(activeHeight - height) < 10) {
        return
      }

      setActiveHeight(height)
    }, [activeHeight, height])

    const wallTexture = useMemo(() => {
      if (
        !isCanvasRefReady ||
        !activeWidth ||
        !activeHeight ||
        !backgroundColor
      ) {
        return null
      }

      return getWallTexture(
        frameSettings.artworkWidth,
        frameSettings.artworkHeight,
        backgroundColor,
      )
    }, [
      activeHeight,
      activeWidth,
      backgroundColor,
      frameSettings.artworkWidth,
      frameSettings.artworkHeight,
      isCanvasRefReady,
    ])

    const fitCameraToFrame = useCallback(() => {
      const targetObject =
        frameSettings.type === 'frame'
          ? frameMeshRef.current
          : passepartoutMeshRef.current

      if (!cameraRef.current || !targetObject) {
        return
      }

      fitCameraToObject(
        cameraRef.current,
        targetObject,
        maxDimension,
        camera.fitRatio,
        THREE.MathUtils.degToRad(rotation),
        camera.fitPerRad,
      )
    }, [camera, frameSettings.type, maxDimension, rotation])

    const debounceUpdateArtworkTexture = useRef(
      debounce(400, async (frameSettings: FrameSettings) => {
        if (!frameSettings.artworkUrl) {
          return
        }

        const textureUrl = await generateTextureUrlFromImage(
          frameSettings.artworkUrl,
          frameSettings.artworkWidth,
          frameSettings.artworkHeight,
        )

        textureLoaderRef.current.load(textureUrl, (texture) => {
          texture.encoding = THREE.sRGBEncoding

          setArtworkUrl(textureUrl)
          setArtworkTexture(texture)

          if (debugArtworkUv && artworkUvViewerRef.current) {
            artworkUvViewerRef.current.setTexture(texture)
          }
        })
      }),
    )

    const debouncedSetMaxDimensionTarget = useRef(
      debounce(400, async (maxDimensionTarget: number) => {
        setMaxDimensionTarget(maxDimensionTarget)
      }),
    )

    const updateFrameTexture = useCallback(
      (forceUpdate = false) => {
        // Unset frame texture
        if (!frameSettings.frameDiffuseUrl) {
          setFrameDiffuseUrl('')
          setFrameDiffuseTexture(null)
          return
        }

        // Set frame texture
        if (
          forceUpdate ||
          !frameDiffuseTexture ||
          frameDiffuseUrl !== frameSettings.frameDiffuseUrl
        ) {
          textureLoaderRef.current.load(
            frameSettings.frameDiffuseUrl,
            (texture) => {
              texture.encoding = THREE.sRGBEncoding
              texture.wrapS = THREE.MirroredRepeatWrapping
              texture.wrapT = THREE.MirroredRepeatWrapping

              setFrameDiffuseUrl(frameSettings.frameDiffuseUrl)
              setFrameDiffuseTexture(texture)

              if (debugFrameUv && frameUvViewerRef.current) {
                frameUvViewerRef.current.setTexture(texture)
              }
            },
          )
        }
      },
      [
        debugFrameUv,
        frameDiffuseTexture,
        frameDiffuseUrl,
        frameSettings.frameDiffuseUrl,
      ],
    )

    const initializeScene = useCallback(() => {
      if (
        !isCanvasRefReady ||
        !canvasRef.current ||
        !activeWidth ||
        !activeHeight
      ) {
        return
      }

      // Create scene, camera and renderer
      sceneRef.current = createScene()
      const canvasAspectRatio =
        canvasRef.current.width / canvasRef.current.height
      cameraRef.current = createCamera(canvasAspectRatio, camera.settings)
      rendererRef.current = createRenderer(
        canvasRef.current,
        activeWidth,
        activeHeight,
        window.devicePixelRatio,
      )

      // Create lights
      const ambientLight = createAmbientLight()
      directionalLightRef.current = createDirectionalLight()
      sceneRef.current.add(ambientLight)
      sceneRef.current.add(directionalLightRef.current)

      // Create geometries
      frameGeometryRef.current = new THREE.Geometry()
      passepartoutGeometryRef.current = new THREE.Geometry()
      passepartoutCoreGeometryRef.current = new THREE.Geometry()
      artworkGeometryRef.current = new THREE.Geometry()

      generateGeometry(
        frameGeometryRef.current,
        passepartoutGeometryRef.current,
        passepartoutCoreGeometryRef.current,
        artworkGeometryRef.current,
        frameSettings,
      )

      // Create materials
      frameMaterialRef.current = new THREE.MeshStandardMaterial({
        color: frameSettings.frameDiffuseUrl ?? 'black',
        roughness: 1,
        metalness: 0,
      })

      if (
        frameSettings.passepartoutTopSize > 0 ||
        frameSettings.passepartoutRightSize > 0 ||
        frameSettings.passepartoutBottomSize > 0 ||
        frameSettings.passepartoutLeftSize > 0
      ) {
        passepartoutMaterialRef.current = new THREE.MeshBasicMaterial({
          color: frameSettings.passepartoutColor,
          reflectivity: 0,
        })
        passepartoutCoreMaterialRef.current = new THREE.MeshBasicMaterial({
          color:
            frameSettings.passepartoutCoreColor ??
            frameSettings.passepartoutColor,
          reflectivity: 0,
        })
      }

      artworkMaterialRef.current = new THREE.MeshStandardMaterial({
        color: 'white',
        roughness: 1,
        metalness: 0,
      })

      // Create meshes
      frameMeshRef.current = new THREE.Mesh(
        frameGeometryRef.current,
        frameMaterialRef.current,
      )
      frameMeshRef.current.castShadow = true
      frameMeshRef.current.receiveShadow = true

      if (
        frameSettings.passepartoutTopSize > 0 ||
        frameSettings.passepartoutRightSize > 0 ||
        frameSettings.passepartoutBottomSize > 0 ||
        frameSettings.passepartoutLeftSize > 0
      ) {
        passepartoutMeshRef.current = new THREE.Mesh(
          passepartoutGeometryRef.current,
          passepartoutMaterialRef.current,
        )
        passepartoutMeshRef.current.castShadow = false
        passepartoutMeshRef.current.receiveShadow = false

        passepartoutCoreMeshRef.current = new THREE.Mesh(
          passepartoutCoreGeometryRef.current,
          passepartoutCoreMaterialRef.current,
        )
        passepartoutCoreMeshRef.current.castShadow = false
        passepartoutCoreMeshRef.current.receiveShadow = false
      }

      artworkMeshRef.current = new THREE.Mesh(
        artworkGeometryRef.current,
        artworkMaterialRef.current,
      )
      artworkMeshRef.current.castShadow = false
      artworkMeshRef.current.receiveShadow = false

      sceneRef.current.add(frameMeshRef.current, artworkMeshRef.current)

      if (
        frameSettings.passepartoutTopSize > 0 ||
        frameSettings.passepartoutRightSize > 0 ||
        frameSettings.passepartoutBottomSize > 0 ||
        frameSettings.passepartoutLeftSize > 0
      ) {
        if (passepartoutMeshRef.current) {
          sceneRef.current.add(passepartoutMeshRef.current)
        }

        if (passepartoutCoreMeshRef.current) {
          sceneRef.current.add(passepartoutCoreMeshRef.current)
        }
      }

      // Create UV viewers
      frameUvViewerRef.current = new UVMapViewer(
        frameMeshRef.current,
        undefined,
        {
          strokeStyle: `red`,
          strokeWidth: 3,
        },
        canvasRef.current,
      )
      artworkUvViewerRef.current = new UVMapViewer(
        artworkMeshRef.current,
        undefined,
        {
          strokeStyle: `red`,
          strokeWidth: 3,
        },
        canvasRef.current,
      )

      fitCameraToFrame()

      // Update artwork image texture
      if (frameSettings.artworkUrl) {
        debounceUpdateArtworkTexture.current(frameSettings)
      }

      updateFrameTexture(true)

      // Create wall
      if (wallTexture) {
        wallMaterialRef.current = new THREE.MeshBasicMaterial({
          map: wallTexture,
        })
        wallGeometryRef.current = new THREE.PlaneGeometry(wallWidth, wallHeight)
        wallMeshRef.current = new THREE.Mesh(
          wallGeometryRef.current,
          wallMaterialRef.current,
        )
        wallMeshRef.current.receiveShadow = true
        // Place directly behind the frame
        wallMeshRef.current.position.set(0, 0, -0.001)

        sceneRef.current.add(wallMeshRef.current)
      }
    }, [
      activeHeight,
      activeWidth,
      canvasRef,
      camera.settings,
      fitCameraToFrame,
      frameSettings,
      isCanvasRefReady,
      updateFrameTexture,
      wallTexture,
    ])

    // Scene initialization hook
    useEffect(() => {
      if (!isCanvasRefReady || !activeWidth || !activeHeight) {
        return
      }

      // Check if width or height has changed
      if (
        activeWidth === lastWidthRef.current &&
        activeHeight === lastHeightRef.current
      ) {
        return
      }

      lastWidthRef.current = activeWidth
      lastHeightRef.current = activeHeight

      initializeScene()
    }, [activeHeight, activeWidth, initializeScene, isCanvasRefReady])

    // Artwork image texture hook
    useEffect(() => {
      if (!frameSettings.artworkUrl) {
        setArtworkUrl(null)
        setArtworkTexture(null)
        return
      }

      if (artworkUrl != frameSettings.artworkUrl || !artworkTexture) {
        debounceUpdateArtworkTexture.current(frameSettings)
      }
    }, [
      frameSettings.artworkUrl,
      frameSettings.artworkWidth,
      frameSettings.artworkHeight,
    ])

    // Artwork mesh hook
    useEffect(() => {
      if (
        !isCanvasRefReady ||
        !activeWidth ||
        !activeHeight ||
        !artworkMaterialRef.current
      ) {
        return
      }

      if (artworkTexture) {
        artworkMaterialRef.current.map = artworkTexture
        // @ts-expect-error Using null is necessary for Three.js to use texture instead of a solid color
        artworkMaterialRef.current.color = null
        artworkMaterialRef.current.needsUpdate = true

        return
      }

      artworkMaterialRef.current.map = null
      artworkMaterialRef.current.color = new THREE.Color('white')
      artworkMaterialRef.current.needsUpdate = true
    }, [activeHeight, activeWidth, artworkTexture, isCanvasRefReady])

    // Wall mesh hook
    useEffect(() => {
      if (
        !isCanvasRefReady ||
        !activeWidth ||
        !activeHeight ||
        !wallMaterialRef.current
      ) {
        return
      }

      if (wallTexture) {
        wallMaterialRef.current.map = wallTexture
        // @ts-expect-error Using null is necessary for Three.js to use texture instead of a solid color
        wallMaterialRef.current.color = null
        wallMaterialRef.current.needsUpdate = true

        return
      }

      wallMaterialRef.current.map = null
      wallMaterialRef.current.color = new THREE.Color('white')
      wallMaterialRef.current.needsUpdate = true
    }, [activeHeight, activeWidth, isCanvasRefReady, wallTexture])

    // Frame texture hook
    useEffect(() => {
      updateFrameTexture()
    }, [updateFrameTexture])

    // Frame mesh hook
    useEffect(() => {
      if (
        !isCanvasRefReady ||
        !activeWidth ||
        !activeHeight ||
        !frameMaterialRef.current
      ) {
        return
      }

      frameMaterialRef.current.map = frameDiffuseTexture ?? null
      // @ts-expect-error Using null is necessary for Three.js to use texture instead of a solid color
      frameMaterialRef.current.color = frameDiffuseTexture
        ? null
        : new THREE.Color(frameSettings.frameDiffuseUrl ?? 'black')
      frameMaterialRef.current.needsUpdate = true
    }, [
      activeHeight,
      activeWidth,
      frameDiffuseTexture,
      frameSettings.frameDiffuseUrl,
      isCanvasRefReady,
    ])

    // Mount size hook
    useEffect(() => {
      if (
        !isCanvasRefReady ||
        !activeWidth ||
        !activeHeight ||
        !sceneRef.current
      ) {
        return
      }

      if (
        frameSettings.passepartoutTopSize > 0 ||
        frameSettings.passepartoutRightSize > 0 ||
        frameSettings.passepartoutBottomSize > 0 ||
        frameSettings.passepartoutLeftSize > 0
      ) {
        if (!passepartoutMaterialRef.current) {
          passepartoutMaterialRef.current = new THREE.MeshBasicMaterial({
            color: frameSettings.passepartoutColor,
            reflectivity: 0,
          })
        }

        if (!passepartoutCoreMaterialRef.current) {
          passepartoutCoreMaterialRef.current = new THREE.MeshBasicMaterial({
            color:
              frameSettings.passepartoutCoreColor ??
              frameSettings.passepartoutColor,
            reflectivity: 0,
          })
        }

        if (!passepartoutMeshRef.current) {
          passepartoutMeshRef.current = new THREE.Mesh(
            passepartoutGeometryRef.current,
            passepartoutMaterialRef.current,
          )
          passepartoutMeshRef.current.receiveShadow = true

          sceneRef.current.add(passepartoutMeshRef.current)
        }

        if (!passepartoutCoreMeshRef.current) {
          passepartoutCoreMeshRef.current = new THREE.Mesh(
            passepartoutCoreGeometryRef.current,
            passepartoutCoreMaterialRef.current,
          )
          passepartoutCoreMeshRef.current.receiveShadow = true

          sceneRef.current.add(passepartoutCoreMeshRef.current)
        }

        return
      }

      if (passepartoutMeshRef.current) {
        sceneRef.current.remove(passepartoutMeshRef.current)
        passepartoutMeshRef.current = undefined
      }

      if (passepartoutCoreMeshRef.current) {
        sceneRef.current.remove(passepartoutCoreMeshRef.current)
        passepartoutCoreMeshRef.current = undefined
      }

      if (passepartoutMaterialRef.current) {
        passepartoutMaterialRef.current.dispose()
        passepartoutMaterialRef.current = undefined
      }

      if (passepartoutCoreMaterialRef.current) {
        passepartoutCoreMaterialRef.current.dispose()
        passepartoutCoreMaterialRef.current = undefined
      }
    }, [
      activeHeight,
      activeWidth,
      frameSettings.passepartoutBottomSize,
      frameSettings.passepartoutColor,
      frameSettings.passepartoutCoreColor,
      frameSettings.passepartoutLeftSize,
      frameSettings.passepartoutRightSize,
      frameSettings.passepartoutTopSize,
      isCanvasRefReady,
    ])

    // Mount color hook
    useEffect(() => {
      if (
        !isCanvasRefReady ||
        !activeWidth ||
        !activeHeight ||
        !passepartoutMaterialRef.current ||
        !passepartoutCoreMaterialRef.current
      ) {
        return
      }

      passepartoutMaterialRef.current.setValues({
        color: frameSettings.passepartoutColor,
      })
      passepartoutCoreMaterialRef.current.setValues({
        color:
          frameSettings.passepartoutCoreColor ??
          frameSettings.passepartoutColor,
      })
    }, [
      activeHeight,
      activeWidth,
      frameSettings.passepartoutColor,
      frameSettings.passepartoutCoreColor,
      isCanvasRefReady,
    ])

    // Frame dimensions hook
    useEffect(() => {
      if (
        !isCanvasRefReady ||
        !activeWidth ||
        !activeHeight ||
        !frameGeometryRef.current ||
        !passepartoutGeometryRef.current ||
        !passepartoutCoreGeometryRef.current ||
        !artworkGeometryRef.current
      ) {
        return
      }

      updateGeometry(
        frameGeometryRef.current,
        passepartoutGeometryRef.current,
        passepartoutCoreGeometryRef.current,
        artworkGeometryRef.current,
        frameSettings,
      )
      fitCameraToFrame()
    }, [
      activeHeight,
      activeWidth,
      frameSettings.artworkHeight,
      frameSettings.artworkWidth,
      frameSettings.frameBottomSize,
      frameSettings.frameInset,
      frameSettings.frameLeftSize,
      frameSettings.frameRightSize,
      frameSettings.frameTopSize,
      frameSettings.passepartoutBottomSize,
      frameSettings.passepartoutLeftSize,
      frameSettings.passepartoutRightSize,
      frameSettings.passepartoutTopSize,
      frameSettings.passepartoutDepth,
      isCanvasRefReady,
    ])

    // Size update hook
    useEffect(() => {
      if (
        !isCanvasRefReady ||
        !activeWidth ||
        !activeHeight ||
        !rendererRef.current ||
        !cameraRef.current ||
        !activeWidth ||
        !activeHeight
      ) {
        return
      }

      cameraRef.current.aspect = activeWidth / activeHeight
      fitCameraToFrame()

      rendererRef.current.setSize(activeWidth, activeHeight)

      // UV debugging viewers
      if (debugFrameUv && frameUvViewerRef.current) {
        frameUvViewerRef.current.setPosition(activeWidth - activeWidth * 0.3, 0)
        frameUvViewerRef.current.setSize(activeWidth * 0.3, activeWidth * 0.3)
      }

      if (debugArtworkUv && artworkUvViewerRef.current) {
        artworkUvViewerRef.current.setSize(activeWidth * 0.3, activeWidth * 0.3)
      }
    }, [
      activeHeight,
      activeWidth,
      debugArtworkUv,
      debugFrameUv,
      isCanvasRefReady,
    ])

    // Maximum object dimensions hook
    useEffect(() => {
      const targetObject =
        frameSettings.type === 'frame'
          ? frameMeshRef.current
          : passepartoutMeshRef.current

      if (!isCanvasRefReady || !activeWidth || !activeHeight || !targetObject) {
        return
      }

      const size = getObjectSizeVector(targetObject)
      const newMaxDimension = Math.max(size.x, size.y, size.z)

      debouncedSetMaxDimensionTarget.current(newMaxDimension)
    }, [
      activeHeight,
      activeWidth,
      frameSettings.artworkHeight,
      frameSettings.artworkWidth,
      frameSettings.frameBottomSize,
      frameSettings.frameLeftSize,
      frameSettings.frameRightSize,
      frameSettings.frameTopSize,
      frameSettings.passepartoutBottomSize,
      frameSettings.passepartoutLeftSize,
      frameSettings.passepartoutRightSize,
      frameSettings.passepartoutTopSize,
      frameSettings.type,
      isCanvasRefReady,
    ])

    // Update camera on maximum object dimension and object rotation changes
    useEffect(() => {
      if (!isCanvasRefReady || !activeWidth || !activeHeight) {
        return
      }

      fitCameraToFrame()
    }, [activeHeight, activeWidth, fitCameraToFrame, isCanvasRefReady])

    // Rerender hook
    useEffect(() => {
      if (
        !isCanvasRefReady ||
        !activeWidth ||
        !activeHeight ||
        !rendererRef.current ||
        !sceneRef.current ||
        !cameraRef.current
      ) {
        return
      }

      rendererRef.current.render(sceneRef.current, cameraRef.current)

      if (debugFrameUv && frameUvViewerRef.current) {
        frameUvViewerRef.current.render(rendererRef.current)
      }

      if (debugArtworkUv && artworkUvViewerRef.current) {
        artworkUvViewerRef.current.render(rendererRef.current)
      }
    }, [
      activeHeight,
      activeWidth,
      artworkTexture,
      debugArtworkUv,
      debugFrameUv,
      frameDiffuseTexture,
      frameSettings,
      isCanvasRefReady,
      maxDimension,
      rotation,
    ])

    return (
      <canvas
        ref={shareRef(canvasRef, forwardedRef)}
        onMouseEnter={() => {
          setRotationTarget(camera.rotationHover)
        }}
        onMouseLeave={() => {
          setRotationTarget(camera.rotationInitial)
        }}
        onTouchStart={() => {
          setRotationTarget(camera.rotationHover)
        }}
        onTouchEnd={() => {
          setRotationTarget(camera.rotationInitial)
        }}
        className={cx('block select-none', className)}
        style={style}
      />
    )
  },
)

FrameViewer.displayName = 'FrameViewer'

export default FrameViewer
